import React, { useMemo, useRef, useEffect } from 'react';
import { useLocale } from 'locale';
import {
  IBulletin,
  ILocalPagePreviewResponse,
  MarketplaceType,
  PocType,
  LeadingAgentFeature,
  Award,
  DealType,
} from 'utils/entities';
import { processImgs } from 'utils/gallery';
import NearbyProjectsSection from 'components/nearby-projects-section';
import { Section as SectionEnum } from 'components/navigation/subheader/SectionsNavigation';
import { ExpandedInsightsSummary } from 'components/expanded-insights-summary';
import AmenitiesChips, { ExtraAmenity } from 'components/unit-page/amenities/amenities-chips';
import AmenitiesCheckList from 'components/unit-page/amenities/amenities-check-list';
import ImageGallery from 'components/unit-page/new-gallery';
import MainInfoWithInvestors from 'components/unit-page/main-info-with-investors';
import MainInfo from 'components/unit-page/main-info';
import { AgentProps } from 'components/unit-page/main-info-with-investors/main-info';
import { ScrollSpyProvider, ScrollSpyTarget } from 'components/scroll-spy';
import { ComplianceSection, InsightsSection, InsightsSectionItem } from 'ds/components/Insights';
import {
  ContentPlaceholder,
  WithContentSlotTopPadding,
  WithContentSlotBottomPadding,
} from 'ds/components/Layout';
import Footer from 'components/footer';
import StarIcon from 'assets/svg/three-star.svg';
import {
  DefaultPrimaryParams,
  DefaultSecondaryParams,
  formatPrimaryAndSecondaryAddress,
  getAddress,
  getDefaultPrimaryParams,
  getDefaultSecondaryParams,
  getUnitCardPrimaryParams,
  renderAddress,
  UnitCardPrimaryParams,
  unitCardTranslateKeys,
  unitPagePrimaryTranslateKeys,
  unitPageSecondaryTranslateKeys,
} from 'utils/addressFormatter';
import { createConfig as createWidgetConfig, widgetAliases } from 'utils/widgetsConfig';
import { Route, useRoute } from 'config/routes';
import withEvents from 'analytics/withEvents';
import { createDetailPageSectionHandlers, DetailPageEventCategory } from 'analytics/handlers/detailPageSectionHandlers';
import ReportListing from 'components/unit-page/report-listing/ReportListing';
import { ScrollRestorer } from 'components/scroll-restorer';
import MadadInfoSection from 'components/unit-page/madad-info';
import Review from 'components/unit-page/reviews';
import { getPrevPrice, isPriceDropped } from 'utils/priceEvents';
import {
  ListingPageContainer,
  ListingInfoWrapper,
  ListingInfo,
  StickyInfo,
  SectionDivider,
  MapSlot,
  InsightsContainer,
  InsightsSectionRow,
  WidgetContainer,
  InsightsHeader,
  BorderlessWidgetContainer,
  MapExpandToggle,
  InsightsMobileWrapper,
  ContactInnerWrap,
  BannerWrapper,
  BannerSection,
  GalleryWrapper,
} from '../styled';
import { useScreenBreakpoint } from 'consts/breakpoints';
import ProjectAdBannerSource from 'components/ads/ProjectAdBannerSource';
import ProjectAdBanner from 'components/ads/ProjectAdBanner';
import { AdGetter } from 'components/ads/types';
import AdditionalListingsCarousel from 'components/private-listing-carousel';
import { ShareButtonSource } from 'components/navigation/share/ShareButton';
import OfficeListingsCarousel from 'components/agent-listing-carousel';
import { getPOCCompanyName, getAgentOfficeId, getPOCMadadSearchResultCategory, getPOCLogo, getPOCName, checkEnabledLeadingAgentFeature, getPOCAvatar } from 'helpers/contact';
import Description from 'components/unit-page/description';
import { SecondaryInfo } from 'components/unit-page/secondary-info/secondary-info';
import SideBarContact from 'components/contact/side-bar';
import { widgets } from 'components/widget';
import { InsightsCollapsedSectionItem } from 'screens/UnitPage/styled';
import Prices from 'components/widget/prices/section';
import { HEADER_HEIGHT } from 'components/navigation/header.constants';
import { MapDialog } from '../MapDialog';
import { MovableMap } from 'components/movable-map';
import MapExpandIcon from 'assets/svg/expand-map-weirdo.svg';
import { MadadInfoHeaderType, MadadHeaderItem } from 'components/unit-page/madad-info/madad-info';
import { SpacingScale } from 'ds/theme/spacing';
import { createListOfIdsForAboutSection, getOverviewAgentType, OverviewDetails } from 'screens/UnitPage/utils';
import FavoritesToggler from 'components/favorites/FavoritesToggler';
import Share from 'components/navigation/share';
import ConnectedPOIStickyContactEntry from 'components/contact/poi-sticky';
import { useShowSticky } from 'hooks/useHideSticky';
import { BackButton } from '../back-button';
import { MobileMapWidget } from 'components/unit-page/mobile-map-widget';
import ConnectedPOIInnerContactEntry from 'components/contact/poi-inner';
import { InsightSection } from 'store/state/insightsContext';
import { ContactAgentModalSource } from 'store/state/app';
import AdditionalAdBannerSource from 'components/ads/AdditionalAdBannerSource';
import { SpacingBlock } from 'components/spacing-block';
import { useStickyBanner } from 'hooks/useStickyBanner';
import PrivateBulletinStickyBar from '../private-bulletin-sticky-bar';
import { useABTests } from 'hooks/useABTests';
import { ListingPageInvestorsInfo } from 'config/abTests';
import PromotionInfoBanner from './promption-info-banner';
import PoalimMortgageCalculator from 'components/poalim/mortgage-calculator';
import { ExtraAmenityType } from 'components/unit-page/amenities/types';
import { getAmenitiesForChecklist } from '../utils';

const agentInfoCarousel = Symbol('agentInfoCarousel');
const officeInfoCarousel = Symbol('officeInfoCarousel');

interface ResidentialListingPageProps {
  bulletin: IBulletin & AgentProps;
  mobileInsightsType: InsightSection;
  loading: boolean;
  activeInsightId: string;
  setVisibleSection: (section: SectionEnum) => void;
  localData: ILocalPagePreviewResponse['searchLocal2'];
  isEditAllowed: boolean;
  isNewUnitPageMapExpanded: boolean;
  setNewUnitPageMapExpanded: (expanded: boolean) => void;
  hasRelatedAgentListings: boolean;
  insightsLoading: boolean;
  showAboutTheOfficeSection: boolean;
  officeListings: IBulletin[];
  officeDescription: string;
  agentDescription: string;
  leadingData: Award;
  isGalleryOpen: boolean;
  agentListings: Record<DealType, IBulletin[]>;
  isAgentConsoleUser: boolean;
  isUser?: boolean;
  investorsTermsAccepted?: boolean;
}

const ResidentialListingPage: React.FunctionComponent<ResidentialListingPageProps> = (props) => {
  const {
    bulletin,
    loading,
    activeInsightId,
    setVisibleSection,
    localData,
    isEditAllowed,
    hasRelatedAgentListings,
    isNewUnitPageMapExpanded,
    setNewUnitPageMapExpanded,
    showAboutTheOfficeSection,
    officeListings,
    officeDescription,
    agentDescription,
    leadingData,
    agentListings,
    insightsLoading,
    mobileInsightsType,
    isGalleryOpen,
    isAgentConsoleUser,
    isUser,
    investorsTermsAccepted,
  } = props;

  const { t } = useLocale();
  const abTests = useABTests();
  const screenBreakpoint = useScreenBreakpoint();
  const scrollAnchor = useRef<HTMLDivElement | null>(null);
  const stickyLevelRef = useRef<HTMLDivElement | null>(null);
  const stickyTabletLevelRef = useRef<HTMLDivElement | null>(null);
  const stickyBannerRef = useRef<HTMLDivElement | null>(null);
  const { route } = useRoute();
  const isInvestorsListing = route && route.params && route.params.investorMode;

  useEffect(() => {
    if (activeInsightId && scrollAnchor.current) {
      scrollAnchor.current.scrollIntoView({ block: 'start' });
    }
  }, [ activeInsightId ]);

  const isMobileInsightsTypePlanning = mobileInsightsType === InsightSection.Planning;
  const isBulletinLoaded = Boolean(bulletin);
  const processedImages = useMemo(() => processImgs(bulletin ? bulletin.images : []), [ bulletin ]);

  const isDesktop = screenBreakpoint >= 3;
  const hideSideBar = screenBreakpoint === 3;
  const mqFrom1to3 = screenBreakpoint < 3;
  const isMobile = screenBreakpoint === 1;

  const pageLoaded = !(insightsLoading && loading);
  const isStickyShow = useShowSticky(screenBreakpoint < 4 ? stickyTabletLevelRef : stickyLevelRef);
  const isBannerVisible = useStickyBanner(stickyBannerRef);

  if (loading || !isBulletinLoaded) {
    return <ContentPlaceholder />;
  }

  const {
    id,
    price,
    dealType,
    area,
    beds,
    floor,
    floors,
    structuredAddress = {},
    poc,
    description,
    locationPoint,
    amenities,
    commonCharges,
    monthlyTaxes,
    eventsHistory,
    addressDetails,
    generalCondition,
    buildingYear,
    availabilityType,
    availableDate,
    buildingClass,
    isAgent,
    isExclusive,
    investorsData,
  } = bulletin;

  const officeName = getPOCCompanyName(poc);
  const officeId = getAgentOfficeId(poc);
  const agentLogo = getPOCAvatar(poc);
  const agentName = getPOCName(poc);
  const overViewAgentType = getOverviewAgentType(bulletin.type, poc.type);
  const showOverviewAgent = overViewAgentType !== OverviewDetails.None;
  const showAboutLeadingAgent = checkEnabledLeadingAgentFeature(leadingData, LeadingAgentFeature.AboutTheAgentInListingPage);
  const primaryAddress = getAddress<DefaultPrimaryParams>(t, renderAddress)(structuredAddress, unitPagePrimaryTranslateKeys, addressDetails.resolutionPreferences, getDefaultPrimaryParams);
  const secondaryAddress = getAddress<DefaultSecondaryParams>(t, renderAddress)(structuredAddress, unitPageSecondaryTranslateKeys, addressDetails.resolutionPreferences, getDefaultSecondaryParams);
  const expandedMapAddress = formatPrimaryAndSecondaryAddress(primaryAddress, secondaryAddress);
  const priceDropped = isPriceDropped(eventsHistory) ? getPrevPrice(eventsHistory) : null;
  const galleryImgAltText = getAddress<UnitCardPrimaryParams>(t, renderAddress)(structuredAddress, unitCardTranslateKeys, addressDetails.resolutionPreferences, getUnitCardPrimaryParams);
  const madadCategory = getPOCMadadSearchResultCategory(poc);
  const officeLogo = getPOCLogo(poc);
  const widgetsList = createWidgetConfig(Route.UnitPage, dealType);

  const showSticky = pageLoaded && isStickyShow;

  const officeMadadHeaderItems: MadadHeaderItem[] = [
    {
      type: MadadInfoHeaderType.MadadBadge,
      madadCategory,
      ml: 0.5 as SpacingScale,
    },
    {
      type: MadadInfoHeaderType.OfficeLogo,
      officeLogo,
      officeId,
      ml: 2 as SpacingScale,
    },
    {
      type: MadadInfoHeaderType.OfficeName,
      officeName,
    },
  ];
  const desktopAgentMadadHeaderItems: MadadHeaderItem[] = [
    {
      type: MadadInfoHeaderType.OfficeLogo,
      officeId,
      officeLogo,
      ml: 2 as SpacingScale,
    },
    {
      type: MadadInfoHeaderType.AgentLogo,
      agentId: poc.type === PocType.Agent ? poc.agentId : null,
      agentLogo,
      ml: 3 as SpacingScale,
    },
    {
      type: MadadInfoHeaderType.AgentInfo,
      agentName,
      agentHint: officeName,
    },
  ];
  const mobileAgentMadadHeaderItems: MadadHeaderItem[] = [
    {
      type: MadadInfoHeaderType.AgentLogo,
      agentId: poc.type === PocType.Agent ? poc.agentId : null,
      agentLogo,
      ml: 3 as SpacingScale,
    },
    {
      type: MadadInfoHeaderType.AgentInfo,
      agentName,
      agentHint: officeName,
    },
  ];

  const gardenAreas = (amenities.additionalAreas && amenities.additionalAreas.gardenAreas) || [];
  const balconyAreas = (amenities.additionalAreas && amenities.additionalAreas.balconyAreas) || [];
  const warehouseAreas = (amenities.additionalAreas && amenities.additionalAreas.warehouseAreas) || [];

  const extraAmenities: ExtraAmenity[] = [
    { type: ExtraAmenityType.Parking, value: bulletin.parking },
    { type: ExtraAmenityType.AirDirection, value: bulletin.airDirections },
    { type: ExtraAmenityType.Bathroom, value: bulletin.bathrooms },
    { type: ExtraAmenityType.Toilet, value: bulletin.toilets },
    { type: ExtraAmenityType.GardenAreas, value: gardenAreas.length },
    amenities.balcony ? null : { type: ExtraAmenityType.BalconyAreas, value: balconyAreas.length },
    amenities.storage ? null : { type: ExtraAmenityType.WarehouseAreas, value: warehouseAreas.length },
  ].filter(Boolean);

  const amenitiesForChecklist = getAmenitiesForChecklist(amenities, {
    balconyAreas,
    warehouseAreas,
    gardenAreas,
    parking: bulletin.parking,
  });

  const isEditPrivateBulletinStickyBarEnabled = (poc && poc.type === PocType.Private) && isEditAllowed && !isGalleryOpen;
  const isContactPoiStickyBarEnabled = !isEditPrivateBulletinStickyBarEnabled && showSticky && !isGalleryOpen;
  const showManageBulletinMenu = isEditAllowed;
  const showEditBtn = isEditAllowed && isAgentConsoleUser;

  const isMainInfoWithInvestors = dealType === DealType.Buy && (
    (!isInvestorsListing && abTests.listingPageInvestorsInfo === ListingPageInvestorsInfo.modeB) ||
    (isInvestorsListing && isUser && investorsTermsAccepted)
  );

  if (mqFrom1to3) {
    return (
      <ProjectAdBannerSource>
        {(getBanner: AdGetter) => (
          <>
            <ScrollSpyProvider onChange={setVisibleSection} rootMargin="-96px 0px 0px -80px">
              <ScrollSpyTarget targetId={SectionEnum.OVERVIEW_HEADER}>
                <div ref={stickyBannerRef} />
                <GalleryWrapper>
                  <PromotionInfoBanner />
                  {locationPoint ? (
                    <ImageGallery
                      type="bulletin"
                      id={id}
                      images={processedImages}
                      enableShare
                      showEditBtn={showEditBtn}
                      showManageBulletinMenu={showManageBulletinMenu}
                      openMap={() => setNewUnitPageMapExpanded(true)}
                      showMapViewToggler={Boolean(locationPoint.lat && locationPoint.lng)}
                      fullScreen={screenBreakpoint < 3}
                      shareSource={ShareButtonSource.ListingPage}
                      showFavoritesBtn={!isEditAllowed}
                      altText={galleryImgAltText}
                    />
                  ) : null}
                </GalleryWrapper>
              </ScrollSpyTarget>
              <ListingPageContainer topPaddingSpacing={0}>
                <div ref={stickyTabletLevelRef} />
                <ListingInfoWrapper>
                  <ListingInfo>
                    <ScrollSpyTarget targetId={SectionEnum.OVERVIEW}>
                      {isMainInfoWithInvestors ? (
                        <MainInfoWithInvestors
                          currentPrice={price}
                          id={id}
                          primaryAddress={primaryAddress}
                          secondaryAddress={secondaryAddress}
                          neighbourhoodDocId={addressDetails.neighbourhoodDocId}
                          dealType={dealType}
                          area={area}
                          investorsData={investorsData}
                          beds={beds}
                          buildingClass={buildingClass}
                          isAgent={isAgent}
                          isExclusive={isExclusive}
                          floor={floor}
                          floors={floors}
                          priceDropped={priceDropped}
                        />
                      ) : (
                        <MainInfo
                          currentPrice={price}
                          primaryAddress={primaryAddress}
                          secondaryAddress={secondaryAddress}
                          dealType={dealType}
                          area={area}
                          beds={beds}
                          buildingClass={buildingClass}
                          isAgent={isAgent}
                          isExclusive={isExclusive}
                          floor={floor}
                          floors={floors}
                          priceDropped={priceDropped}
                        />
                      )}

                      {isDesktop ? <SectionDivider /> : null}
                      <AmenitiesChips amenities={amenities} extraAmenities={extraAmenities} children={<SectionDivider />} />
                      {description ? (
                        <>
                          <Description description={description} />
                          <SectionDivider />
                        </>
                      ) : null}
                    </ScrollSpyTarget>
                    <ScrollSpyTarget targetId={SectionEnum.SHOW_MORE}>
                      <AmenitiesCheckList amenities={amenitiesForChecklist} />
                      <SectionDivider />
                      <SecondaryInfo
                        currentPrice={price}
                        generalCondition={generalCondition}
                        monthlyTaxes={monthlyTaxes}
                        availableDate={availableDate}
                        availabilityType={availabilityType}
                        isFurnished={amenities.isFurnished}
                        buildingOnStilts={amenities.buildingOnStilts}
                        buildingYear={buildingYear}
                        commonCharges={commonCharges}
                        gardenAreas={gardenAreas}
                        balconyAreas={balconyAreas}
                        warehouseAreas={warehouseAreas}
                        area={area}
                        floors={floors}
                      />

                      {dealType === DealType.Buy ? <PoalimMortgageCalculator price={bulletin.price} /> : <SectionDivider />}
                      <MobileMapWidget showTo={3} customMapAction={() => setNewUnitPageMapExpanded(true)} locationPoint={locationPoint} withBottomMargin={!showOverviewAgent} />

                      <ContactInnerWrap>
                        {screenBreakpoint === 1 ? <SideBarContact source={ContactAgentModalSource.ContactSection} id={bulletin.id} /> : <ConnectedPOIInnerContactEntry id={id} />}
                        <ReportListing />
                      </ContactInnerWrap>
                      <ProjectAdBanner eventSource="unit_page_top_banner" ad={getBanner()} mTop={3} fullWidth />
                    </ScrollSpyTarget>
                  </ListingInfo>
                </ListingInfoWrapper>
              </ListingPageContainer>

              <InsightsMobileWrapper>
                <ScrollSpyTarget targetId={SectionEnum.IMPORTANT_INFO}>
                  <InsightsHeader>
                    {t('expandedHighlight.back')}
                    <StarIcon weight={24} height={24} />
                  </InsightsHeader>

                  {activeInsightId ? <ExpandedInsightsSummary newDesign id={bulletin.id} /> : (
                    locationPoint ? (
                      <>
                        <BorderlessWidgetContainer>
                          <Prices />
                        </BorderlessWidgetContainer>
                        {widgetsList.map((item: widgetAliases) => (
                          <WidgetContainer key={item}>
                            <InsightsCollapsedSectionItem
                              data-auto={'collapsed-' + item}
                              className="insight-collapsed-row"
                            >
                              {widgets(item, { isUnitPage: true })}
                            </InsightsCollapsedSectionItem>
                          </WidgetContainer>
                        ))}
                      </>
                  ) : null)}
                </ScrollSpyTarget>
              </InsightsMobileWrapper>

              {isContactPoiStickyBarEnabled ? <ConnectedPOIStickyContactEntry id={bulletin.id} isBannerVisible={isBannerVisible} /> : null}
              {isEditPrivateBulletinStickyBarEnabled ? <PrivateBulletinStickyBar bulletin={bulletin} /> : null}

              <ListingPageContainer>
                <ProjectAdBanner eventSource="unit_page_top_banner" ad={getBanner()} fullWidth />
              </ListingPageContainer>

              {localData && localData.userRatings ? (
                <ListingPageContainer>
                  <ScrollSpyTarget targetId={SectionEnum.UGC}>
                    <Review
                      ratings={localData.userRatings}
                      localPageDocId={addressDetails.neighbourhoodDocId ? addressDetails.neighbourhoodDocId : addressDetails.cityDocId}
                    />
                  </ScrollSpyTarget>
                </ListingPageContainer>
              ) : null}

              <InsightsSection isSolidGrey>
                <ListingPageContainer>
                  <WithContentSlotTopPadding>

                    {poc.type === PocType.Agent && showAboutLeadingAgent ? (
                      <ScrollSpyTarget targetId={SectionEnum.ABOUT_THE_AGENT}>
                        <WithContentSlotBottomPadding data-auto="about-agent">
                          <MadadInfoSection
                            type="agent"
                            from={agentName}
                            title={t('madad.agent.info.title.copy')}
                            madadHeaderItems={isDesktop ? desktopAgentMadadHeaderItems : mobileAgentMadadHeaderItems}
                            description={agentDescription}
                            moreLinkRoute={Route.AgentPage}
                            moreLinkParamsId={poc.agentId}
                            moreLinkCopy={t('madad.agent.info.more.copy')}
                            carouselKey={agentInfoCarousel}
                            marketplace={MarketplaceType.Residential}
                            listingsIds={createListOfIdsForAboutSection(agentListings ? agentListings[dealType] : [], id)}
                          />
                        </WithContentSlotBottomPadding>
                      </ScrollSpyTarget>
                    ) : null}

                    {officeId ? (
                      showAboutTheOfficeSection ? (
                        <WithContentSlotBottomPadding data-auto="about-office">
                          <ScrollSpyTarget targetId={SectionEnum.ABOUT_THE_OFFICE}>
                            <MadadInfoSection
                              type="office"
                              from={officeName}
                              title={t('madad.office.info.title.copy')}
                              madadHeaderItems={officeMadadHeaderItems}
                              description={officeDescription}
                              moreLinkRoute={Route.OfficePage}
                              moreLinkParamsId={officeId}
                              moreLinkCopy={t('madad.office.info.more.copy')}
                              carouselKey={officeInfoCarousel}
                              marketplace={MarketplaceType.Residential}
                              listingsIds={createListOfIdsForAboutSection(officeListings, id)}
                              trackingEventSource="about_office"
                            />
                          </ScrollSpyTarget>
                        </WithContentSlotBottomPadding>
                      ) : (
                        <OfficeListingsCarousel
                          officeId={officeId}
                          officeName={officeName}
                          excludePoiId={id}
                          isListingModeB
                          marketplace={MarketplaceType.Residential}
                        />
                      )
                    ) : null}

                    {hasRelatedAgentListings ? (
                      <InsightsSectionItem>
                        <ScrollSpyTarget targetId={SectionEnum.PROPERTY_CAROUSEL}>
                          <AdditionalListingsCarousel />
                        </ScrollSpyTarget>
                      </InsightsSectionItem>
                    ) : null}

                    <InsightsSectionItem>
                      <NearbyProjectsSection
                        term={addressDetails.cityDocId}
                        zoneName={addressDetails.city}
                        dealType={dealType}
                      />
                    </InsightsSectionItem>
                  </WithContentSlotTopPadding>
                </ListingPageContainer>
              </InsightsSection>

              <ListingPageContainer>
                <ComplianceSection isInsightSectionPresent={Boolean(locationPoint)} collapsedWrapper isContactModeB={isDesktop}>
                  <Footer noPadding={mqFrom1to3} alignItems="right" />
                </ComplianceSection>
              </ListingPageContainer>
              <ScrollRestorer restore={!activeInsightId} />
              {isNewUnitPageMapExpanded ? (
                <MapDialog
                  showOnlyMap
                  hideCloseBtn={isMobileInsightsTypePlanning}
                  favoriteToggler={(
                    <FavoritesToggler controlButtonMode="transparent" id={id} type={bulletin.type} tooltip={false} withButton noText isRound />
                  )}
                  shareButton={(
                    <Share menuZIndex={10005} controlButtonMode="transparent" alignment="right" tooltip={false} isRound source={ShareButtonSource.ListingPage}/>
                  )}
                  address={expandedMapAddress}
                  onClose={() => setNewUnitPageMapExpanded(false)}
                />
              ) : null}
            </ScrollSpyProvider>
          </>
        )}
      </ProjectAdBannerSource>
    );
  }

  return (
    <ProjectAdBannerSource>
      {(getBanner: AdGetter) => (
        <ScrollSpyProvider onChange={setVisibleSection} rootMargin="-64px 0px -150px 0px">
          <ListingPageContainer>
            <BackButton />
            <ScrollSpyTarget targetId={SectionEnum.OVERVIEW_HEADER}>
              <GalleryWrapper>
                <PromotionInfoBanner />
                {locationPoint ? (
                  <ImageGallery
                    type="bulletin"
                    id={id}
                    images={processedImages}
                    enableShare
                    shareSource={ShareButtonSource.ListingPage}
                    showEditBtn={showEditBtn}
                    showManageBulletinMenu={showManageBulletinMenu}
                    showFavoritesBtn={!isEditAllowed}
                    altText={galleryImgAltText}
                  />
                ) : null}
              </GalleryWrapper>
            </ScrollSpyTarget>
            <div ref={stickyTabletLevelRef} />
            <ListingInfoWrapper>
              <ListingInfo>
                {!isMobile &&
                  <>
                    <AdditionalAdBannerSource>
                      {(getAdditionalBanner: AdGetter) => (
                        <ProjectAdBanner
                          eventSource="unit_page_additional_banner"
                          ad={getAdditionalBanner()}
                          mTop={1}
                          height="auto"
                          fullWidth
                        />
                      )}
                    </AdditionalAdBannerSource>
                    <SpacingBlock mBottom={4} />
                  </>
                }
                <ScrollSpyTarget targetId={SectionEnum.OVERVIEW}>
                  {isMainInfoWithInvestors ? (
                    <MainInfoWithInvestors
                      id={id}
                      currentPrice={price}
                      primaryAddress={primaryAddress}
                      secondaryAddress={secondaryAddress}
                      neighbourhoodDocId={addressDetails.neighbourhoodDocId}
                      dealType={dealType}
                      area={area}
                      investorsData={investorsData}
                      beds={beds}
                      buildingClass={buildingClass}
                      isAgent={isAgent}
                      isExclusive={isExclusive}
                      floor={floor}
                      floors={floors}
                      priceDropped={priceDropped}
                    />
                  ) : (
                    <MainInfo
                      currentPrice={price}
                      primaryAddress={primaryAddress}
                      secondaryAddress={secondaryAddress}
                      dealType={dealType}
                      area={area}
                      beds={beds}
                      buildingClass={buildingClass}
                      isAgent={isAgent}
                      isExclusive={isExclusive}
                      floor={floor}
                      floors={floors}
                      priceDropped={priceDropped}
                    />
                  )}
                  {isDesktop ? <SectionDivider /> : null}
                  <AmenitiesChips amenities={amenities} extraAmenities={extraAmenities} children={<SectionDivider />} />
                  {description ? (
                    <>
                      <Description description={description} />
                      <SectionDivider />
                    </>
                  ) : null}
                </ScrollSpyTarget>
                <ScrollSpyTarget targetId={SectionEnum.SHOW_MORE}>
                  <AmenitiesCheckList amenities={amenitiesForChecklist} />
                  {dealType === DealType.Buy ? <PoalimMortgageCalculator price={bulletin.price} /> : <SectionDivider />}
                  <SecondaryInfo
                    currentPrice={price}
                    generalCondition={generalCondition}
                    monthlyTaxes={monthlyTaxes}
                    availableDate={availableDate}
                    availabilityType={availabilityType}
                    isFurnished={amenities.isFurnished}
                    buildingOnStilts={amenities.buildingOnStilts}
                    buildingYear={buildingYear}
                    commonCharges={commonCharges}
                    area={area}
                    gardenAreas={gardenAreas}
                    balconyAreas={balconyAreas}
                    warehouseAreas={warehouseAreas}
                    floors={floors}
                  />
                  <ReportListing />
                  <SectionDivider />
                  <ProjectAdBanner eventSource="unit_page_top_banner" ad={getBanner()} mTop={4} mBottom={4} fullWidth />
                </ScrollSpyTarget>
              </ListingInfo>

              {hideSideBar ? null : (
                <StickyInfo>
                  <SideBarContact source={ContactAgentModalSource.ContactSection} id={bulletin.id} />
                </StickyInfo>
              )}
            </ListingInfoWrapper>
          </ListingPageContainer>

          <InsightsSectionRow>
            <InsightsContainer>
              <ScrollSpyTarget targetId={SectionEnum.IMPORTANT_INFO} maxAspectRatio={0.15} />
              <InsightsHeader>
                {t('expandedHighlight.back')}
                <StarIcon weight={24} height={24} />
              </InsightsHeader>

              <div ref={scrollAnchor} style={{ position: 'relative', top: -HEADER_HEIGHT }} />
              {activeInsightId ? <ExpandedInsightsSummary newDesign id={bulletin.id} /> : (
                locationPoint ? (
                  <>
                    <ScrollSpyTarget targetId={SectionEnum.PRICES_ESTIMATION}>
                      <BorderlessWidgetContainer>
                        <Prices />
                      </BorderlessWidgetContainer>
                    </ScrollSpyTarget>

                    <ScrollSpyTarget targetId={SectionEnum.EXPLORE_THE_AREA}>
                      {widgetsList.map((item: widgetAliases) => (
                        <WidgetContainer key={item}>
                          <InsightsCollapsedSectionItem
                            data-auto={'collapsed-' + item}
                            className="insight-collapsed-row"
                          >
                            {widgets(item, { isUnitPage: true })}
                          </InsightsCollapsedSectionItem>
                        </WidgetContainer>
                      ))}
                    </ScrollSpyTarget>
                  </>
              ) : null)}
            </InsightsContainer>

            <MapSlot>
              <MovableMap />
              <MapExpandToggle onClick={() => setNewUnitPageMapExpanded(true)}>
                <MapExpandIcon />
              </MapExpandToggle>
              <div ref={stickyLevelRef} style={{ position: 'absolute', bottom: '20px' }} />
            </MapSlot>
          </InsightsSectionRow>

          {isContactPoiStickyBarEnabled ? <ConnectedPOIStickyContactEntry id={bulletin.id} /> : null}
          {isEditPrivateBulletinStickyBarEnabled ? <PrivateBulletinStickyBar bulletin={bulletin} /> : null}

          {localData && localData.userRatings ? (
            <ListingPageContainer>
              <ScrollSpyTarget targetId={SectionEnum.UGC}>
                <Review
                  ratings={localData.userRatings}
                  localPageDocId={addressDetails.neighbourhoodDocId ? addressDetails.neighbourhoodDocId : addressDetails.cityDocId}
                />
              </ScrollSpyTarget>
            </ListingPageContainer>
          ) : null}

            <BannerSection>
              <BannerWrapper>
                <ProjectAdBanner eventSource="unit_page_top_banner" ad={getBanner()} mTop={4} mBottom={4} fullWidth />
              </BannerWrapper>
            </BannerSection>

          <InsightsSection isSolidGrey>
            <ListingPageContainer>
              <WithContentSlotTopPadding>

                {poc.type === PocType.Agent && showAboutLeadingAgent ? (
                  <ScrollSpyTarget targetId={SectionEnum.ABOUT_THE_AGENT}>
                    <WithContentSlotBottomPadding data-auto="about-agent">
                      <MadadInfoSection
                        type="agent"
                        from={agentName}
                        title={t('madad.agent.info.title.copy')}
                        madadHeaderItems={isDesktop ? desktopAgentMadadHeaderItems : mobileAgentMadadHeaderItems}
                        description={agentDescription}
                        moreLinkRoute={Route.AgentPage}
                        moreLinkParamsId={poc.agentId}
                        moreLinkCopy={t('madad.agent.info.more.copy')}
                        carouselKey={agentInfoCarousel}
                        marketplace={MarketplaceType.Residential}
                        listingsIds={createListOfIdsForAboutSection(agentListings ? agentListings[dealType] : [], id)}
                      />
                    </WithContentSlotBottomPadding>
                  </ScrollSpyTarget>
                ) : null}

                {officeId ? (
                  showAboutTheOfficeSection ? (
                    <WithContentSlotBottomPadding data-auto="about-office">
                      <ScrollSpyTarget targetId={SectionEnum.ABOUT_THE_OFFICE}>
                        <MadadInfoSection
                          type="office"
                          from={officeName}
                          title={t('madad.office.info.title.copy')}
                          madadHeaderItems={officeMadadHeaderItems}
                          description={officeDescription}
                          moreLinkRoute={Route.OfficePage}
                          moreLinkParamsId={officeId}
                          moreLinkCopy={t('madad.office.info.more.copy')}
                          carouselKey={officeInfoCarousel}
                          marketplace={MarketplaceType.Residential}
                          listingsIds={createListOfIdsForAboutSection(officeListings, id)}
                          trackingEventSource="about_office"
                        />
                      </ScrollSpyTarget>
                    </WithContentSlotBottomPadding>
                  ) : (
                    <OfficeListingsCarousel
                      officeId={officeId}
                      officeName={officeName}
                      excludePoiId={id}
                      isListingModeB
                      marketplace={MarketplaceType.Residential}
                    />
                  )
                ) : null}

                {hasRelatedAgentListings ? (
                  <InsightsSectionItem>
                    <ScrollSpyTarget targetId={SectionEnum.PROPERTY_CAROUSEL}>
                      <AdditionalListingsCarousel />
                    </ScrollSpyTarget>
                  </InsightsSectionItem>
                ) : null}

                <InsightsSectionItem>
                  <NearbyProjectsSection
                    term={addressDetails.cityDocId}
                    zoneName={addressDetails.city}
                    dealType={dealType}
                  />
                </InsightsSectionItem>
              </WithContentSlotTopPadding>
            </ListingPageContainer>
          </InsightsSection>

          <ListingPageContainer>
            <ComplianceSection isInsightSectionPresent={Boolean(locationPoint)} collapsedWrapper isContactModeB={isDesktop}>
              <Footer noPadding={mqFrom1to3} alignItems="right" />
            </ComplianceSection>
          </ListingPageContainer>
          <ScrollRestorer restore={!activeInsightId} />
          {isNewUnitPageMapExpanded ? (
            <MapDialog
              showOnlyMap={false}
              favoriteToggler={(
                <FavoritesToggler controlButtonMode="transparent" id={id} type={bulletin.type} tooltip={false} withButton noText isRound />
              )}
              shareButton={(
                <Share menuZIndex={10005} controlButtonMode="transparent" alignment="right" tooltip={false} isRound source={ShareButtonSource.ListingPage}/>
              )}
              address={expandedMapAddress}
              onClose={() => setNewUnitPageMapExpanded(false)}
            />
          ) : null}
        </ScrollSpyProvider>
      )}
    </ProjectAdBannerSource>
  );
};

export default withEvents<ResidentialListingPageProps>(sendEvent => {
  const sectionHandlers = createDetailPageSectionHandlers(DetailPageEventCategory.Property)(sendEvent);
  return {
    ...sectionHandlers,
    setNewUnitPageMapExpanded(isExpanded: boolean) {
      sendEvent('map_toggle_click', 'map', {
        event: {
          'toggle_direction': isExpanded ? 'expand' : 'collapse',
        },
      });
    },
  };
})(ResidentialListingPage);
